import { SessionApiClient } from "@/natsstubs/session/client";
import type {
  SetIterationsRequest,
  StickerTypeInput,
  UpdateSwimlanesRequest,
} from "@/natsstubs/session/models";
import { StickyTypeApiClient } from "@/natsstubs/sticky_type/client";
import type {
  CreateStickyTypeRequest,
  UpdateStickyTypeRequest,
} from "@/natsstubs/sticky_type/models";
import type { CamelToSnake } from "@/natsstubs/wire/transform";
import { keysToSnake } from "@/natsstubs/wire/transform";

import type { NatsRpcBackend } from "../NatsRpcBackend";
import type { UnknownRecord } from "../RpcBackend";

export function registerSessionAdapters(nats: NatsRpcBackend): void {
  const client = new SessionApiClient(nats.caller);
  const stickyTypeClient = new StickyTypeApiClient(nats.caller);
  const id = (args: unknown[]): string => String(args[0]);

  nats.register("session", "core", {
    delete: async (args: unknown[]) => client.deleteSession(id(args)),

    archive: async (args: unknown[]) => client.archiveSession(id(args)),

    unarchive: async (args: unknown[]) => client.unarchiveSession(id(args)),

    import_csv: async (args: unknown[]) =>
      client.loadBacklogCsv({ rawData: args[1] as string }, id(args)),

    import_team_csv: async (args: unknown[]) =>
      client.loadTeamCsv({ rawData: args[1] as string }, id(args)),

    import_jalign_xlsx: async (args: unknown[]) =>
      client.loadJalignXlsx({ rawData: args[1] as string }, id(args)),

    get_name: async (args: unknown[]) => (await client.getName(id(args))).name,

    set_name: async (args: unknown[]) => client.setName({ name: args[1] as string }, id(args)),

    get_planning_dates: async (args: unknown[]) => client.getPlanningDates(id(args)),

    set_planning_dates: async (args: unknown[]) =>
      client.setPlanningDates({ start: args[1] as string, end: args[2] as string }, id(args)),

    set_settings: async (args: unknown[]) =>
      client.setSettings(
        { key: args[1] as string, value: args[2] as string | number | boolean },
        id(args),
      ),

    get_sticker_types: async (args: unknown[]) =>
      (await client.getStickerTypes(id(args))).stickerTypes,

    set_sticker_types: async (args: unknown[]) =>
      (await client.setStickerTypes({ stickerTypes: args[1] as StickerTypeInput[] }, id(args)))
        .stickerTypes,

    get_participating_users: async (args: unknown[]) =>
      (await client.getParticipatingUsers(id(args))).teamIds,

    set_participating_users: async (args: unknown[]) =>
      client.setParticipatingUsers({ teamIds: args[1] as number[] }, id(args)),

    update_swimlanes: async (args: unknown[]) =>
      client.updateSwimlanes({ edits: args[1] as UpdateSwimlanesRequest["edits"] }, id(args)),
    set_iterations: async (args: unknown[]) =>
      client.setIterations({ iterations: args[1] as SetIterationsRequest["iterations"] }, id(args)),

    create_link_type: async (args: unknown[]) =>
      (
        await client.createLinkType(
          {
            fromStickyType: args[1] as string,
            toStickyType: args[2] as string,
          },
          id(args),
        )
      ).linkType.id,

    get_link_types: async (args: unknown[]) =>
      keysToSnake((await client.getLinkTypes(id(args))).linkTypes ?? []),

    get_teams: async (args: unknown[]) =>
      keysToSnake((await client.getTeams(id(args))).teams ?? []),

    get_arts: async (args: unknown[]) => keysToSnake((await client.getArts(id(args))).arts ?? []),

    get_iterations: async (args: unknown[]) =>
      keysToSnake(
        ((await client.getIterations(id(args))).iterations ?? []).map((iteration) => ({
          ...iteration,
          teams: iteration.teams ?? [],
        })),
      ),

    get_boards: async (args: unknown[]) =>
      keysToSnake(
        ((await client.getBoards(id(args))).boards ?? []).map(({ id: boardId, ...rest }) => ({
          boardId,
          ...rest,
        })),
      ),

    get_settings: async (args: unknown[]) => (await client.getSettings(id(args))).settings ?? {},

    search_stickies: async (args: unknown[]) =>
      keysToSnake(
        (await client.searchStickies({ query: args[1] as UnknownRecord }, id(args))).stickies ?? [],
      ),

    alter_link_type: async (args: unknown[], _kwargs?: UnknownRecord, sid?: string) =>
      client.updateLinkType(
        {
          fromStickyType: (args[1] as { from_sticky_type?: string }).from_sticky_type,
          toStickyType: (args[1] as { to_sticky_type?: string }).to_sticky_type,
        },
        String(sid),
        String(args[0]),
      ),

    delete_link_type: async (args: unknown[], _kwargs?: UnknownRecord, sid?: string) =>
      client.deleteLinkType(String(sid), String(args[0])),

    has_stickies_of_type: async (args: unknown[], _kwargs?: UnknownRecord, sid?: string) =>
      (await client.hasStickiesOfType({ stickyTypeId: String(args[0]) }, String(sid))).hasStickies,

    update_sticky_type: async (args: unknown[]) =>
      stickyTypeClient.updateStickyType(
        { properties: args[1] as UpdateStickyTypeRequest["properties"] },
        String(args[0]),
      ),

    // The WAMP wire runs every document through db_response/add_needed_filed
    // (rentouch-shared), so sticky types arrive with BOTH `_id` and `id`.
    // Consumers depend on either key (linkService reads `_id`, loadFlexBoards
    // reads `id`) — alias `_id` to keep the NATS shape WAMP-compatible.
    get_sticky_types: async (args: unknown[]) =>
      keysToSnake((await stickyTypeClient.getStickyTypes(String(args[0]))).stickyTypes).map(
        (stickyType) => ({ ...stickyType, _id: stickyType.id }),
      ),

    create_sticky_type: async (args: unknown[]) => {
      const sticky = args[5] as Record<string, unknown>;
      const almType = args[4] as { id?: string } | null;
      // rawRequest endpoint: payload is the snake wire shape, sent untransformed
      // so opaque ALM keys in alm_type_config (jira_WSJF_field, ac_*_elementName,
      // ado referenceName) survive. alm_type stays the id reference; the backend
      // stores alm_type_config into the almsync session.
      const payload = {
        sticky_type: {
          ...sticky,
          id: (sticky.id as string | undefined) ?? "",
          session_id: String(args[0]),
          alm_type: almType?.id,
        },
        alm_type_config: almType ?? undefined,
      };
      return (
        await stickyTypeClient.createStickyType(
          payload as CamelToSnake<CreateStickyTypeRequest>,
          String(args[0]),
        )
      ).stickyType.id;
    },
  });
}
